import React from 'react';
import { FlaskConical, Dna, Zap, ArrowRight, Fingerprint } from 'lucide-react';

const PRODUCTS = [
  {
    id: 'blood-test',
    title: '10X Health Blood Test',
    badge: 'Recommended',
    description: 'The gateway to functional medicine. A full-panel blood test covering hormones, metabolic markers, and inflammation — with a 1:1 provider consult and personalized protocols.',
    features: [
      'Doctor-prescribed custom protocols',
      'Hormone, metabolic & inflammation panel',
      '1:1 telehealth consult (all 50 states)',
      'Lab Corp locations nationwide',
    ],
    whyPeptides: 'Before starting any peptide protocol, you need baseline labs for IGF-1, testosterone, thyroid, metabolic panel, and inflammatory markers. This test covers all of them — plus you get a licensed provider to review your results.',
    steps: ['Order your test', 'Draw at local Lab Corp', 'Meet your provider via telehealth', 'Start your personalized protocol'],
    icon: FlaskConical,
    color: '#00F0FF',
    href: 'https://10xhealthsystem.com/blood-test/?utm_source=ninjathlete',
    btnText: 'Order Blood Test',
    featured: true,
  },
  {
    id: 'genetic-test',
    title: '10X Health Genetic Test',
    badge: null,
    description: '44% of the population has a genetic mutation affecting the 5-MTHFR gene. This at-home swab test analyzes your major actionable genes to reveal nutrient deficiencies, methylation issues, and why supplements may not be working.',
    features: [
      'At-home cotton swab — no needles',
      'One-time test (genes don\'t change)',
      '5 major gene analysis (MTHFR, COMT, etc.)',
      'Personalized supplement protocol',
    ],
    whyPeptides: 'Genetic mutations can affect how your body methylates and processes peptides. If your MTHFR gene is mutated, you may not be converting nutrients properly — impacting peptide efficacy and recovery.',
    icon: Dna,
    color: '#a855f7',
    href: 'https://10xhealthnetwork.com/pages/genetic-testing?utm_source=ninjathlete',
    btnText: 'Get Genetic Test',
  },
  {
    id: 'precision-genetics',
    title: '10X Precision Genetics',
    badge: null,
    description: 'Your body is different from anyone else\'s. Precision genetics maps your unique nutritional needs — showing exactly which macro ratios, vitamins, and supplements your genes respond to best. Includes a 300-page personalized report.',
    features: [
      '300-page personalized nutrition roadmap',
      'Precision supplement microbeads (slow-release)',
      'Macro ratio optimization (carbs vs protein vs fat)',
      'Personalized to your DNA, not generic guidelines',
    ],
    whyPeptides: 'Peptide protocols work best when your nutrition is dialed in. If you\'re eating the wrong macros for your genetics, your body can\'t fully support the repair and growth signals peptides trigger.',
    icon: Fingerprint,
    color: '#22c55e',
    href: 'https://10xhealthnetwork.com/pages/precision-genetics?utm_source=ninjathlete',
    btnText: 'Get Precision Test',
  },
  {
    id: 'superhuman-protocol',
    title: 'Superhuman Protocol',
    badge: null,
    description: 'A 3-step biohacking system using magnetism (PEMF), oxygen (EWOT), and light (photobiomodulation). Designed to restore cellular function, boost circulation by up to 900%, and accelerate recovery.',
    features: [
      'Step 1: PEMF mat (8-10 min) — enhanced blood flow',
      'Step 2: Exercise with oxygen training (8-10 min)',
      'Step 3: Infrared light therapy bed (10-12 min)',
      '~30 min total session',
    ],
    whyPeptides: 'Enhanced circulation from PEMF and oxygen therapy means peptides reach target tissues faster and more efficiently. Light therapy synergizes with BPC-157 and TB-500\'s tissue repair mechanisms.',
    icon: Zap,
    color: '#f59e0b',
    href: 'https://10xhealthnetwork.com/pages/super-human-protocol?utm_source=ninjathlete',
    btnText: 'Learn More',
  },
];

export default function TenXProductCards() {
  return (
    <div className="my-12 space-y-6" data-testid="10x-product-cards">
      <div className="border-l-2 border-[#00F0FF] pl-5 mb-8">
        <h3 className="font-heading text-2xl sm:text-3xl text-white mb-2">
          BASELINE TESTING: WHERE TO START
        </h3>
        <p className="text-sm text-zinc-400 leading-relaxed">
          Before starting any peptide protocol, baseline bloodwork and genetic testing aren't optional — they're essential. These are the tools we recommend for a data-driven approach.
        </p>
      </div>

      {PRODUCTS.map(product => {
        const Icon = product.icon;
        return (
          <div key={product.id}
            className={`border ${product.featured ? 'border-[#00F0FF]/30 bg-[#00F0FF]/[0.02]' : 'border-zinc-800 bg-zinc-900/20'} overflow-hidden`}
            data-testid={`product-card-${product.id}`}>
            {/* Header */}
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border flex items-center justify-center flex-shrink-0" style={{ borderColor: `${product.color}30`, background: `${product.color}08` }}>
                  <Icon className="w-5 h-5" style={{ color: product.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-lg text-white font-semibold">{product.title}</h4>
                    {product.badge && (
                      <span className="text-[8px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 border" style={{ color: product.color, borderColor: `${product.color}40`, background: `${product.color}10` }}>
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{product.description}</p>
                </div>
              </div>

              {/* Features */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-1.5 pl-14">
                {product.features.map((f, i) => (
                  <div key={f} className="flex items-start gap-2">
                    <span className="text-[8px] mt-1.5 flex-shrink-0" style={{ color: product.color }}>&#9679;</span>
                    <span className="text-xs text-zinc-300">{f}</span>
                  </div>
                ))}
              </div>

              {/* Why for peptides */}
              <div className="mt-4 ml-14 p-3 bg-zinc-900/40 border-l-2" style={{ borderColor: `${product.color}40` }}>
                <p className="text-[10px] font-mono tracking-widest uppercase mb-1" style={{ color: product.color }}>Why This Matters for Peptide Users</p>
                <p className="text-xs text-zinc-400 leading-relaxed">{product.whyPeptides}</p>
              </div>

              {/* Steps (blood test only) */}
              {product.steps && (
                <div className="mt-4 ml-14 flex flex-wrap gap-2">
                  {product.steps.map((s, i) => (
                    <div key={s} className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500">
                      <span className="w-5 h-5 bg-zinc-800 flex items-center justify-center text-[#00F0FF] text-[9px] font-bold flex-shrink-0">{i + 1}</span>
                      {s}
                      {i < product.steps.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-zinc-700 ml-1" />}
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="mt-5 ml-14">
                <a href={product.href} target="_blank" rel="nofollow sponsored noreferrer"
                  className={`inline-flex items-center gap-1.5 px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-wider transition-colors ${
                    product.featured
                      ? 'bg-[#00F0FF] text-black hover:bg-[#00d6e0]'
                      : 'bg-zinc-900 border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white'
                  }`}
                  data-testid={`product-cta-${product.id}`}>
                  {product.btnText} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        );
      })}

      <p className="text-[9px] text-zinc-700 flex items-center gap-1">
        Affiliate links — NinjAthlete may earn a commission at no extra cost to you.
      </p>
    </div>
  );
}
